import { useEffect, useState, useRef } from 'react'
import s from './styles.module.scss'
import { head } from 'helpers'
import { Alert, Layouts, Loader, Input, TextArea, Form } from 'components'
import * as api from 'api'

export default function Main() {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [fetching, setFetching] = useState(false)
  const [settings, setSettings] = useState(null)
  const [stats, setStats] = useState(null)
  const processing = useRef(false)

  // Form fields
  const [keywords, setKeywords] = useState('')
  const [timePeriod, setTimePeriod] = useState(30)
  const [rssFeeds, setRssFeeds] = useState('')
  const [newsApiKey, setNewsApiKey] = useState('')
  const [isEnabled, setIsEnabled] = useState(true)
  const [fetchFrequency, setFetchFrequency] = useState(6)
  const [maxItemsPerFetch, setMaxItemsPerFetch] = useState(20)
  const [autoApprove, setAutoApprove] = useState(false)
  const [language, setLanguage] = useState('en')
  const [country, setCountry] = useState('in')

  useEffect(() => {
    head({ title: 'News Feed Settings | BCN Admin' })
    fetchSettings()
    fetchStats()
  }, [])

  const fetchSettings = async () => {
    if (processing.current) return
    processing.current = true
    setLoading(true)

    try {
      const res = await api.admin.newsFeedSettings.get()
      if (res.code === 200 && res.payload?.settings) {
        const s = res.payload.settings
        setSettings(s)
        setKeywords(s.keywords?.join(', ') || '')
        setTimePeriod(s.timePeriod || 30)
        setRssFeeds(s.rssFeeds?.join('\n') || '')
        setNewsApiKey(s.newsApiKey || '')
        setIsEnabled(s.isEnabled !== false)
        setFetchFrequency(s.fetchFrequency || 6)
        setMaxItemsPerFetch(s.maxItemsPerFetch || 20)
        setAutoApprove(s.autoApprove || false)
        setLanguage(s.language || 'en')
        setCountry(s.country || 'in')
      } else {
        Alert.error(res.message || 'Failed to load settings')
      }
    } catch (error) {
      console.error('Error fetching settings:', error)
      Alert.error('Failed to load settings')
    }

    setLoading(false)
    processing.current = false
  }

  const fetchStats = async () => {
    try {
      const res = await api.admin.newsFeedSettings.getStats()
      if (res.code === 200 && res.payload) {
        setStats(res.payload)
      }
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  const handleSave = async () => {
    if (processing.current || saving) return
    processing.current = true
    setSaving(true)

    try {
      const keywordsArray = keywords
        .split(',')
        .map(k => k.trim())
        .filter(k => k.length > 0)

      const rssFeedsArray = rssFeeds
        .split('\n')
        .map(f => f.trim())
        .filter(f => f.length > 0 && f.startsWith('http'))

      const updateData = {
        keywords: keywordsArray,
        timePeriod: Number(timePeriod),
        rssFeeds: rssFeedsArray,
        newsApiKey: newsApiKey.trim(),
        isEnabled,
        fetchFrequency: Number(fetchFrequency),
        maxItemsPerFetch: Number(maxItemsPerFetch),
        autoApprove,
        language,
        country
      }

      const res = await api.admin.newsFeedSettings.update(updateData)
      if (res.code === 200) {
        Alert.success('Settings saved successfully')
        fetchSettings()
        fetchStats()
      } else {
        Alert.error(res.message || 'Failed to save settings')
      }
    } catch (error) {
      console.error('Error saving settings:', error)
      Alert.error('Failed to save settings')
    }

    setSaving(false)
    processing.current = false
  }

  const handleTriggerFetch = async () => {
    if (processing.current || fetching) return
    processing.current = true
    setFetching(true)

    try {
      const res = await api.admin.newsFeedSettings.triggerFetch()
      if (res.code === 200) {
        Alert.success(
          `Fetch completed! Fetched: ${res.payload?.fetched || 0}, Saved: ${res.payload?.saved || 0}`
        )
        fetchStats()
      } else {
        Alert.error(res.message || 'Failed to fetch news')
      }
    } catch (error) {
      console.error('Error triggering fetch:', error)
      Alert.error('Failed to fetch news')
    }

    setFetching(false)
    processing.current = false
  }

  return (
    <div className={s.main}>
      <Layouts.Classic title='News Feed Settings' />
      <div className={s.content}>
        {loading ? (
          <Loader />
        ) : (
          <>
          {/* Statistics Card */}
          {stats && (
            <div className={s.statsCard}>
              <h3 className={s.statsTitle}>Statistics</h3>
              <div className={s.statsGrid}>
                <div className={s.statItem}>
                  <div className={s.statLabel}>Total Fetched</div>
                  <div className={s.statValue}>{stats.totalFetched || 0}</div>
                </div>
                <div className={s.statItem}>
                  <div className={s.statLabel}>Pending Approval</div>
                  <div className={s.statValue}>{stats.pendingApproval || 0}</div>
                </div>
                <div className={s.statItem}>
                  <div className={s.statLabel}>Approved</div>
                  <div className={s.statValue}>{stats.approved || 0}</div>
                </div>
                <div className={s.statItem}>
                  <div className={s.statLabel}>Last Fetch</div>
                  <div className={s.statValue}>
                    {stats.lastFetchedAt
                      ? new Date(stats.lastFetchedAt).toLocaleString()
                      : 'Never'}
                  </div>
                </div>
                <div className={s.statItem}>
                  <div className={s.statLabel}>Status</div>
                  <div className={s.statValue}>
                    <span
                      className={
                        stats.lastFetchStatus === 'success'
                          ? s.statusSuccess
                          : stats.lastFetchStatus === 'failed'
                          ? s.statusFailed
                          : s.statusPending
                      }
                    >
                      {stats.lastFetchStatus || 'pending'}
                    </span>
                  </div>
                </div>
              </div>
              {stats.lastFetchError && (
                <div className={s.errorMessage}>Error: {stats.lastFetchError}</div>
              )}
            </div>
          )}

          {/* Settings Form */}
          <div className={s.formCard}>
            <h3 className={s.formTitle}>Configuration</h3>

            <Form className={s.form}>
              {/* Enable/Disable */}
              <div className={s.formGroup}>
                <label className={s.checkboxLabel}>
                  <input
                    type='checkbox'
                    checked={isEnabled}
                    onChange={e => setIsEnabled(e.target.checked)}
                  />
                  <span>Enable News Feed Aggregation</span>
                </label>
              </div>

              {/* Keywords */}
              <div className={s.formGroup}>
                <label>Keywords (comma-separated)</label>
                <TextArea.Classic
                  value={keywords}
                  onChange={e => setKeywords(e.target.value)}
                  placeholder='brahmins, sanatana, hindu, vedic, dharma'
                  rows={2}
                />
                <small>News items must contain at least one of these keywords</small>
              </div>

              {/* Time Period */}
              <div className={s.formGroup}>
                <label>Time Period (days)</label>
                <Input.Classic
                  type='number'
                  value={timePeriod}
                  onChange={e => setTimePeriod(e.target.value)}
                  min={1}
                  max={365}
                />
                <small>Only fetch news from the last N days</small>
              </div>

              {/* RSS Feeds */}
              <div className={s.formGroup}>
                <label>RSS Feed URLs (one per line)</label>
                <TextArea.Classic
                  value={rssFeeds}
                  onChange={e => setRssFeeds(e.target.value)}
                  placeholder='https://www.thehindu.com/news/feeder/default.rss&#10;https://timesofindia.indiatimes.com/rssfeeds/-2128838597.cms'
                  rows={4}
                />
                <small>Enter RSS feed URLs, one per line</small>
              </div>

              {/* News API Key */}
              <div className={s.formGroup}>
                <label>News API Key (optional)</label>
                <Input.Classic
                  type='text'
                  value={newsApiKey}
                  onChange={e => setNewsApiKey(e.target.value)}
                  placeholder='Your NewsAPI.org API key'
                />
                <small>
                  Get your free API key from{' '}
                  <a href='https://newsapi.org/' target='_blank' rel='noopener noreferrer'>
                    newsapi.org
                  </a>
                </small>
              </div>

              {/* Fetch Frequency */}
              <div className={s.formGroup}>
                <label>Fetch Frequency (hours)</label>
                <Input.Classic
                  type='number'
                  value={fetchFrequency}
                  onChange={e => setFetchFrequency(e.target.value)}
                  min={1}
                  max={168}
                />
                <small>How often to automatically fetch news (1-168 hours)</small>
              </div>

              {/* Max Items */}
              <div className={s.formGroup}>
                <label>Max Items Per Fetch</label>
                <Input.Classic
                  type='number'
                  value={maxItemsPerFetch}
                  onChange={e => setMaxItemsPerFetch(e.target.value)}
                  min={1}
                  max={100}
                />
                <small>Maximum number of news items to fetch per run</small>
              </div>

              {/* Auto Approve */}
              <div className={s.formGroup}>
                <label className={s.checkboxLabel}>
                  <input
                    type='checkbox'
                    checked={autoApprove}
                    onChange={e => setAutoApprove(e.target.checked)}
                  />
                  <span>Auto-approve fetched news</span>
                </label>
                <small>If unchecked, news will require manual approval</small>
              </div>

              {/* Language */}
              <div className={s.formGroup}>
                <label>Language</label>
                <select value={language} onChange={e => setLanguage(e.target.value)}>
                  <option value='en'>English</option>
                  <option value='hi'>Hindi</option>
                </select>
              </div>

              {/* Country */}
              <div className={s.formGroup}>
                <label>Country</label>
                <select value={country} onChange={e => setCountry(e.target.value)}>
                  <option value='in'>India</option>
                  <option value='us'>United States</option>
                  <option value='all'>All</option>
                </select>
              </div>

              {/* Actions */}
              <div className={s.actions}>
                <button
                  className={s.saveButton}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
                <button
                  className={s.fetchButton}
                  onClick={handleTriggerFetch}
                  disabled={fetching || !isEnabled}
                >
                  {fetching ? 'Fetching...' : 'Fetch News Now'}
                </button>
              </div>
            </Form>
          </div>
          </>
        )}
      </div>
    </div>
  )
}

